import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, User, Lock, AlertTriangle, LogIn, Eye, EyeOff, Clock, CheckCircle2, XCircle } from 'lucide-react';

interface CashierSession {
  cashier: { id: number; name: string; username: string; role?: 'manager' | 'cashier' | 'inventory' | 'readonly'; roleLabel?: string };
  // JWT migration: apiKey removed from stored session — fetched at runtime via /api/my-store
  store: { id: number; name: string };
  token?: string;
}

interface POSCashierLoginProps {
  onSuccess: (session: CashierSession) => void;
}

const SESSION_KEY = 'pos_cashier_session';

export function getCashierSession(): CashierSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearCashierSession() {
  localStorage.removeItem(SESSION_KEY);
  // 🛡️ Phase 5 — clear mirrored JWT too
  localStorage.removeItem('kangavena:cashierToken');
}

export default function POSCashierLogin({ onSuccess }: POSCashierLoginProps) {
  const [storeCode, setStoreCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Phase 6 — Pending manager approval
  const [pendingState, setPendingState] = useState<{ requestId: number; cashierInfo: CashierSession['cashier']; storeInfo: CashierSession['store'] } | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<'waiting' | 'rejected' | 'expired'>('waiting');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!pendingState) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`/api/cashier/login-request/${pendingState.requestId}`);
        const d = await r.json();
        if (d.status === 'APPROVED' && d.token) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          const session: CashierSession = {
            cashier: pendingState.cashierInfo,
            store: pendingState.storeInfo,
            token: d.token,
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));
          localStorage.setItem('kangavena:cashierToken', d.token);
          onSuccess(session);
        } else if (d.status === 'REJECTED') {
          clearInterval(pollRef.current!); pollRef.current = null;
          setApprovalStatus('rejected');
        } else if (d.status === 'EXPIRED') {
          clearInterval(pollRef.current!); pollRef.current = null;
          setApprovalStatus('expired');
        }
      } catch { /* ignore poll network errors */ }
    }, 2500);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [pendingState]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!storeCode.trim() || !username.trim() || !password) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/cashier/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeCode: storeCode.trim().toUpperCase(), username: username.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); return; }
      // Phase 6 — Server now returns PENDING for all cashier logins
      if (data.pending && data.requestId) {
        setPendingState({ requestId: data.requestId, cashierInfo: data.cashier, storeInfo: data.store });
        setApprovalStatus('waiting');
        return;
      }
      // Direct token path (e.g., manager-role bypass or future direct-login config)
      const session: CashierSession = {
        cashier: data.cashier,
        store: { id: data.store.id, name: data.store.name },
        token: data.token,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      if (data.token) { localStorage.setItem('kangavena:cashierToken', data.token); }
      onSuccess(session);
    } catch {
      setError('Cannot reach server. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetToLogin = () => {
    setPendingState(null);
    setApprovalStatus('waiting');
    setError('');
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-[#04060f] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-500/6 blur-[140px]" />
        <div className="absolute -bottom-32 right-0 w-[400px] h-[400px] rounded-full bg-violet-500/5 blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm relative z-10"
      >
        {/* Badge */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <Zap size={12} className="text-amber-400" />
            <span className="text-[11px] font-black tracking-[0.2em] text-amber-300 uppercase">POS.OS · Cashier Login</span>
          </div>
        </div>

        {/* Phase 6 — Pending approval waiting card */}
        {pendingState && (
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl">
            <div className="px-6 py-8 flex flex-col items-center text-center gap-4">
              {approvalStatus === 'waiting' ? (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                    <Clock size={26} className="text-amber-400 animate-pulse" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-white mb-1">Waiting for Approval</h2>
                    <p className="text-[11px] text-slate-500 font-mono">Hi <span className="text-amber-300 font-bold">{pendingState.cashierInfo.name}</span>, your login request has been sent to the manager.</p>
                    <p className="text-[10px] text-slate-600 font-mono mt-1">Please wait for manager to approve your session…</p>
                  </div>
                  <div className="flex gap-1.5">
                    {[0,1,2].map(i => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-amber-500/60 animate-bounce" style={{ animationDelay: `${i * 0.2}s` }} />
                    ))}
                  </div>
                </>
              ) : approvalStatus === 'rejected' ? (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
                    <XCircle size={26} className="text-rose-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-white mb-1">Login Denied</h2>
                    <p className="text-[11px] text-slate-500 font-mono">Your login request was rejected by the manager.</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-slate-500/10 border border-slate-500/20 flex items-center justify-center">
                    <Clock size={26} className="text-slate-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-white mb-1">Request Expired</h2>
                    <p className="text-[11px] text-slate-500 font-mono">Approval request timed out. Please try again.</p>
                  </div>
                </>
              )}
              {approvalStatus !== 'waiting' && (
                <button
                  onClick={resetToLogin}
                  className="mt-2 px-5 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[11px] font-bold uppercase tracking-widest hover:bg-amber-500/20 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        )}

        {/* Card */}
        {!pendingState && (
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl">
          <div className="px-6 pt-6 pb-2">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
              <User size={22} className="text-amber-400" />
            </div>
            <h1 className="text-xl font-black text-white mb-1">Cashier Sign In</h1>
            <p className="text-[11px] text-slate-600 font-mono">Enter your store credentials to begin your shift</p>
          </div>

          <form onSubmit={handleLogin} className="p-6 space-y-4">
            {/* Store Code */}
            <div className="space-y-1.5">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Store Code</label>
              <input
                value={storeCode}
                onChange={e => setStoreCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
                placeholder="e.g. TECH or RAT01"
                maxLength={16}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-[14px] text-amber-300 placeholder:text-slate-700 outline-none focus:border-amber-500/40 font-mono tracking-[0.2em] uppercase transition-colors"
              />
            </div>

            {/* Username */}
            <div className="space-y-1.5">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Username</label>
              <div className="relative">
                <User size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-700" />
                <input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="your_username"
                  autoComplete="username"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-9 pr-4 py-3 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-amber-500/40 font-mono transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Password</label>
              <div className="relative">
                <Lock size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-700" />
                <input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-9 pr-10 py-3 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-amber-500/40 font-mono transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-700 hover:text-slate-400 transition-colors"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-red-500/8 border border-red-500/20 text-red-400 text-[11px] font-mono"
                >
                  <AlertTriangle size={12} className="shrink-0" /> {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[12px] uppercase tracking-widest bg-gradient-to-r from-amber-500 to-orange-500 text-black disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_24px_rgba(245,158,11,0.25)] hover:shadow-[0_0_36px_rgba(245,158,11,0.4)] transition-shadow"
            >
              {loading
                ? <><span className="w-3.5 h-3.5 border-2 border-black/40 border-t-black rounded-full animate-spin" /> Signing in…</>
                : <><LogIn size={14} /> Start Shift</>
              }
            </motion.button>
          </form>

          <div className="px-6 pb-5 text-center">
            <p className="text-[9px] text-slate-700 font-mono">
              Manager access? Go to{' '}
              <a href="/pos/admin" className="text-violet-500 hover:text-violet-400 transition-colors">/pos/admin</a>
            </p>
          </div>
        </div>
        )}

        <p className="text-center text-[9px] text-slate-800 font-mono mt-4">
          Kangavena POS.OS · Powered by Luna AI
        </p>
      </motion.div>
    </div>
  );
}
